import React, { useState, useEffect, useRef } from 'react'
import { Loader2, AlertTriangle, Check, Clock, X } from 'lucide-react'
import { DurationButton } from './DurationButton.js'
import { bookRoom } from '../utils/calendar-utils.js'

export interface BookingModalProps {
    isOpen: boolean;
    onClose: () => void;
    roomId: string;
    roomName: string;
    selectedTime: Date | null;
}

export function BookingModal({ isOpen, onClose, roomId, roomName, selectedTime }: BookingModalProps) {
    const [selectedDuration, setSelectedDuration] = useState<number>(15)
    const [isBooking, setIsBooking] = useState(false)
    const [bookingSuccess, setBookingSuccess] = useState(false)
    const [bookingError, setBookingError] = useState<string | null>(null)
    const modalRef = useRef<HTMLDivElement>(null)

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedDuration(15)
            setBookingSuccess(false)
            setBookingError(null)
        }
    }, [isOpen])

    // Handle clicks outside the modal and escape key
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose()
            }
        }

        function handleEscapeKey(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        // Add event listeners when modal is shown
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleEscapeKey)
        }

        // Clean up
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEscapeKey)
        }
    }, [isOpen, onClose])

    const handleBookRoom = async () => {
        if (!roomId || !selectedTime) return

        setIsBooking(true)
        setBookingSuccess(false)
        setBookingError(null)

        try {
            const response = await bookRoom(
                roomId,
                selectedTime,
                selectedDuration,
                `Booked from ${roomName} calendar`
            )

            if (!response.ok) {
                throw new Error('Failed to book room')
            }

            setBookingSuccess(true)

            // Reset success state and close modal after 2 seconds
            setTimeout(() => {
                setBookingSuccess(false)
                onClose()
            }, 2000)
        } catch (err) {
            setBookingError(err instanceof Error ? err.message : 'Failed to book room')

            // Clear error after 4 seconds
            setTimeout(() => {
                setBookingError(null)
            }, 4000)
        } finally {
            setIsBooking(false)
        }
    }

    if (!isOpen || !selectedTime) {
        return null
    }

    return (
        <div className="fixed inset-0 bg-black/[.5] flex items-center justify-center z-50 p-4">
            <div
                ref={modalRef}
                className="bg-white rounded-[20px] p-6 max-w-md w-full shadow-lg"
            >
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold">Book Room</h3>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                            <span className="font-medium">Room:</span>
                            <span>{roomName}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="font-medium">Time:</span>
                            <span>{selectedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <div className="grid grid-cols-2 gap-2">
                        <DurationButton
                            duration={15}
                            isSelected={selectedDuration === 15}
                            onClick={() => setSelectedDuration(15)}
                        />
                        <DurationButton
                            duration={30}
                            isSelected={selectedDuration === 30}
                            onClick={() => setSelectedDuration(30)}
                        />
                        <DurationButton
                            duration={45}
                            isSelected={selectedDuration === 45}
                            onClick={() => setSelectedDuration(45)}
                        />
                        <DurationButton
                            duration={55}
                            isSelected={selectedDuration === 55}
                            onClick={() => setSelectedDuration(55)}
                        />
                    </div>
                </div>

                <button
                    className={`cursor-pointer w-full h-10 px-4 py-2 rounded-full flex items-center justify-center gap-2 ${
                        isBooking
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : bookingSuccess
                                ? 'bg-green-600 text-white'
                                : 'bg-black text-white'
                    }`}
                    onClick={handleBookRoom}
                    disabled={isBooking}
                >
                    {isBooking ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Booking...</span>
                        </>
                    ) : bookingSuccess ? (
                        <>
                            <Check className="w-4 h-4" />
                            <span>Booked!</span>
                        </>
                    ) : (
                        <>
                            <Clock className="w-4 h-4" />
                            <span>Book</span>
                        </>
                    )}
                </button>

                {bookingError && (
                    <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        <span>{bookingError}</span>
                    </div>
                )}
            </div>
        </div>
    )
}
